import { assertSportsbookHistoryDetail } from '../contracts/sportsbookHistoryResponse.contract.js';

const authoritativeVersion = (row) => {
  const version = row?.row_version;
  if (typeof version !== 'string' || !/^[1-9]\d{0,18}$/.test(version)) {
    throw new TypeError('history row_version must be a bounded positive integer');
  }
  return version;
};
const laterVersion = (left, right) => left.length > right.length || (left.length === right.length && left > right);

export const sportsbookHistoryDetailCacheKey = (row) => {
  if (typeof row?.history_id !== 'string' || row.history_id === '') throw new Error('history_id is required');
  if (typeof row?.row_version !== 'string' || row.row_version === '') throw new Error('row_version is required');
  return `${row.history_id}|${row.row_version}`;
};

const detailVersionMismatch = () => Object.assign(
  new Error('목록과 다른 버전의 베팅상세를 받았습니다. 목록을 새로고침해 주세요.'),
  { code: 'SPORTSBOOK_HISTORY_DETAIL_VERSION_MISMATCH' },
);

export const createSportsbookHistoryDetailCache = ({ requestDetail }) => {
  const cache = new Map();
  const activeRequests = new Map();
  const generations = new Map();

  const deleteCachedHistory = (historyId) => {
    const prefix = `${historyId}|`;
    for (const key of cache.keys()) {
      if (key.startsWith(prefix)) cache.delete(key);
    }
  };

  const nextGeneration = (historyId) => {
    const generation = (generations.get(historyId) || 0) + 1;
    generations.set(historyId, generation);
    return generation;
  };

  const abortRequest = (historyId) => {
    const active = activeRequests.get(historyId);
    if (!active) return;
    active.controller.abort();
    activeRequests.delete(historyId);
    nextGeneration(historyId);
  };

  const load = (row, { force = false } = {}) => {
    const historyId = row.history_id;
    const key = sportsbookHistoryDetailCacheKey(row);
    const cached = cache.get(key);
    if (cached && !force) return Promise.resolve(cached);

    const active = activeRequests.get(historyId);
    if (active && active.key === key && !force) return active.promise;
    if (active) abortRequest(historyId);
    deleteCachedHistory(historyId);

    const controller = new AbortController();
    const generation = nextGeneration(historyId);
    const request = { key, controller, generation, promise: null };
    request.promise = Promise.resolve(requestDetail(historyId, controller.signal)).then((detail) => {
      if (controller.signal.aborted || generations.get(historyId) !== generation) return null;
      if (detail?.history_id !== historyId) throw new Error('history detail identity mismatch');
      const responseKey = sportsbookHistoryDetailCacheKey(detail);
      if (responseKey !== key) throw detailVersionMismatch();
      deleteCachedHistory(historyId);
      cache.set(key, detail);
      return detail;
    }).finally(() => {
      if (activeRequests.get(historyId) === request) activeRequests.delete(historyId);
    });
    activeRequests.set(historyId, request);
    return request.promise;
  };

  const syncRows = (rows) => {
    const validKeys = new Map(rows.map((row) => [row.history_id, sportsbookHistoryDetailCacheKey(row)]));
    for (const [historyId, request] of activeRequests) {
      if (validKeys.get(historyId) !== request.key) abortRequest(historyId);
    }
    for (const key of cache.keys()) {
      const separator = key.lastIndexOf('|');
      const historyId = separator < 0 ? '' : key.slice(0, separator);
      if (validKeys.get(historyId) !== key) cache.delete(key);
    }
  };

  const invalidate = (historyId) => {
    abortRequest(historyId);
    deleteCachedHistory(historyId);
  };

  const acceptAuthoritativeDetail = (row, detail) => {
    assertSportsbookHistoryDetail(detail);
    const key = sportsbookHistoryDetailCacheKey(detail);
    if (row?.history_id !== detail.history_id || detail.status !== 'CANCELLED' || detail.capabilities.can_cancel !== false) {
      throw new TypeError('matching confirmed cancellation detail is required');
    }
    const version = authoritativeVersion(detail);
    const historyId = detail.history_id;
    if (laterVersion(authoritativeVersion(row), version)) return false;
    for (const cached of cache.values()) {
      if (cached.history_id === historyId && laterVersion(authoritativeVersion(cached), version)) return false;
    }
    const active = activeRequests.get(historyId);
    if (active && laterVersion(authoritativeVersion({ row_version: active.key.slice(active.key.lastIndexOf('|') + 1) }), version)) return false;
    abortRequest(historyId);
    deleteCachedHistory(historyId);
    cache.set(key, detail);
    return true;
  };

  const abortAll = () => {
    for (const historyId of [...activeRequests.keys()]) abortRequest(historyId);
  };

  return {
    load,
    syncRows,
    invalidate,
    acceptAuthoritativeDetail,
    abortAll,
    peek: (row) => cache.get(sportsbookHistoryDetailCacheKey(row)) || null,
  };
};
